import { useAnalytics } from '@inflearn/analytics';
import { useCallback, useMemo } from 'react';

import { NEXT_PUBLIC_MIXPANEL_TOKEN } from '../../../appConfig';
import useQueryParams from '../../hooks/useQueryParams';
import useUserProfileQuery from '../../hooks/useUserProfileQuery';
import useClientAccountProfileQuery from '../../queries/useClientAccountProfileQuery';
import type { EventNames, EventPropsMap } from './eventNames';

export function useTrackProfileEvent() {
  const { id } = useQueryParams();
  const { analytics } = useAnalytics();
  const { data: session, isLoading: isSessionLoading } = useClientAccountProfileQuery();
  const { data: profile, isLoading: isProfileLoading } = useUserProfileQuery({ id: Number(id) });

  const userProperty = useMemo(() => {
    if (!session?.id) {
      return {
        user_id: null,
        user_name: null,
        user_email: null,
        role_b2b: null,
        role_b2c: null,
        role_instructor: null,
        role_admin: null,
      };
    }

    return {
      user_id: session.id,
      user_name: session.name,
      user_email: session.email,
      role_b2b: session.group !== null,
      role_b2c: session.group == null,
      role_instructor: session.isInstructor,
      role_admin: session.isAdmin,
    };
  }, [session]);

  const profileProperty = useMemo(() => {
    if (!profile) {
      return {};
    }

    return {
      user_identification: profile?.id,
      is_instructor: profile?.isInstructor,
    };
  }, [profile]);

  const trackMixpanelEvent = useCallback(
    <K extends EventNames>(
      eventName: K,
      props?: EventPropsMap[K] extends undefined ? never : Partial<EventPropsMap[K]>,
    ) => {
      void analytics.track(eventName, {
        mixpanelToken: NEXT_PUBLIC_MIXPANEL_TOKEN,
        mxp___user: userProperty,
        ...profileProperty,
        ...props,
      });
    },
    [analytics, userProperty],
  );

  return { trackMixpanelEvent, isReady: !isProfileLoading && !isSessionLoading };
}
